// src/pages/PublicProfile.jsx - Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, { useState, useEffect, useRef } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiMapPin,
  FiBriefcase,
  FiStar,
  FiAward,
  FiHeart,
  FiTrendingUp,
  FiExternalLink,
  FiMail,
  FiInstagram,
  FiTwitter,
  FiLinkedin,
  FiGithub,
  FiDribbble,
  FiFacebook,
  FiYoutube,
  FiGrid,
  FiArrowRight,
  FiUser,
  FiCalendar,
  FiSmile,
  FiCheck,
  FiZap,
  FiShield,
  FiGlobe,
  FiBookOpen,
  FiCpu,
  FiLayers,
  FiTarget,
  FiEye,
  FiCode,
  FiPenTool,
  FiMonitor,
  FiSmartphone,
  FiPackage,
  FiDatabase,
  FiCloud,
  FiLock,
  FiUsers,
  FiBriefcase as FiBriefcaseIcon,
  FiClock,
  FiAward as FiAwardIcon,
  FiTrendingUp as FiTrendingUpIcon,
  FiStar as FiStarIcon,
  FiMessageCircle,
  FiThumbsUp,
  FiCheckCircle,
  FiArrowUp,
} from 'react-icons/fi';
import { RiVerifiedBadgeFill } from 'react-icons/ri';
import { TbBrandFiverr } from 'react-icons/tb';
import { FaUpwork, FaBehance } from 'react-icons/fa6';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import StatsSection from '../components/StatsSection';
import ReviewsSection from '../components/ReviewsSection';
import { FiverrCard } from '../components/FiverrCard';
// ✅ REMOVED: ogImageService - No client-side OG generation
// import { ogImageService } from '../services/ogImageService';

// =============================================
// GLASSY CARD
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        borderRadius: '16px',
        boxShadow: 'none',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/3 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// GLASSY DIVIDER
// =============================================
const GlassyDivider = () => {
  return (
    <div className="relative py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="relative h-px w-full overflow-hidden">
          <motion.div
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute inset-0 w-1/3"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--text), transparent)',
              opacity: 0.06,
            }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================
// SKILL BADGE WITH ANIMATION - Black & White
// =============================================
const SkillBadge = ({ skill, index }) => {
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      viewport={{ once: true }}
      className="group relative"
    >
      <div
        className="px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default"
        style={{
          backgroundColor: 'var(--bg)',
          color: 'var(--text)',
          border: '1px solid var(--border)',
        }}
      >
        {skill}
        <motion.div
          className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background:
              'radial-gradient(circle at center, var(--text) 0%, transparent 70%)',
            opacity: 0.03,
          }}
        />
      </div>
    </motion.span>
  );
};

// =============================================
// VALUE CARD - Black & White
// =============================================
const ValueCard = ({ icon: Icon, title, description }) => {
  return (
    <motion.div whileHover={{ y: -4 }} className="group">
      <GlassyCard className="p-6 text-center rounded-2xl transition-all duration-300 hover:shadow-xl">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
          style={{
            backgroundColor: 'var(--border)',
            color: 'var(--text)',
          }}
        >
          <Icon className="w-7 h-7" />
        </div>
        <h4
          className="font-semibold text-base mb-2"
          style={{ color: 'var(--text)' }}
        >
          {title}
        </h4>
        <p
          className="text-sm leading-relaxed"
          style={{ color: 'var(--muted)' }}
        >
          {description}
        </p>
        <motion.div
          className="w-8 h-0.5 mx-auto mt-3 rounded-full"
          style={{ backgroundColor: 'var(--text)', opacity: 0.3 }}
          initial={{ width: 0 }}
          whileInView={{ width: '2rem' }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        />
      </GlassyCard>
    </motion.div>
  );
};

function PublicProfile() {
  const { username } = useParams();
  const { actualTheme } = useTheme();
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountCreatedDate, setAccountCreatedDate] = useState('');
  const [hoveredSocial, setHoveredSocial] = useState(null);

  const fetchAccountCreationDate = async userId => {
    if (!userId) return;
    try {
      if (currentUser?.metadata?.creationTime && currentUser.uid === userId) {
        const createdDate = new Date(currentUser.metadata.creationTime);
        setAccountCreatedDate(
          createdDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
        );
      } else {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.createdAt?.toDate) {
            setAccountCreatedDate(
              data.createdAt.toDate().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }),
            );
          } else if (data.joinDate) {
            setAccountCreatedDate(data.joinDate);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching account creation date:', error);
      setAccountCreatedDate('January 2024');
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        let userDocData = null;
        let userId = null;
        const usersRef = collection(db, 'users');

        let q = query(usersRef, where('username', '==', username));
        let querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          userDocData = {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data(),
          };
          userId = querySnapshot.docs[0].id;
        }

        if (!userDocData && !username.startsWith('@')) {
          q = query(usersRef, where('username', '==', `@${username}`));
          querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            userDocData = {
              id: querySnapshot.docs[0].id,
              ...querySnapshot.docs[0].data(),
            };
            userId = querySnapshot.docs[0].id;
          }
        }

        if (!userDocData && username.startsWith('@')) {
          q = query(usersRef, where('username', '==', username.substring(1)));
          querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            userDocData = {
              id: querySnapshot.docs[0].id,
              ...querySnapshot.docs[0].data(),
            };
            userId = querySnapshot.docs[0].id;
          }
        }

        if (userDocData) {
          setUserData(userDocData);
          await fetchAccountCreationDate(userId);
        } else {
          setError('User not found');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchUserProfile();
    } else {
      setError('No username provided');
      setLoading(false);
    }
  }, [username, currentUser]);

  // Client-focused value propositions - Black & White
  const clientValues = [
    {
      icon: FiTarget,
      title: 'Strategic Design',
      description:
        'Designs that align with your business goals and drive results.',
    },
    {
      icon: FiClock,
      title: 'On-Time Delivery',
      description:
        'Reliable delivery with clear timelines and regular updates.',
    },
    {
      icon: FiMessageCircle,
      title: 'Clear Communication',
      description:
        'Transparent collaboration with regular check-ins and feedback.',
    },
    {
      icon: FiShield,
      title: 'Quality Assurance',
      description:
        'Every project is reviewed for quality, consistency, and impact.',
    },
  ];

  // Social platforms
  const socialPlatforms = [
    {
      id: 'instagram',
      label: 'Instagram',
      icon: FiInstagram,
      color: '#E4405F',
    },
    { id: 'twitter', label: 'Twitter', icon: FiTwitter, color: '#1DA1F2' },
    { id: 'linkedin', label: 'LinkedIn', icon: FiLinkedin, color: '#0A66C2' },
    { id: 'github', label: 'GitHub', icon: FiGithub, color: '#333' },
    { id: 'dribbble', label: 'Behance', icon: FaBehance, color: '#1769FF' },
    { id: 'fiverr', label: 'Fiverr', icon: TbBrandFiverr, color: '#1DBF73' },
    { id: 'upwork', label: 'Upwork', icon: FaUpwork, color: '#6FDA44' },
    { id: 'facebook', label: 'Facebook', icon: FiFacebook, color: '#1877F2' },
  ];

  // Generate schema for profile
  const profileSchema = userData
    ? {
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        name: `${userData.fullName} - Designer Profile`,
        description: userData.bio || 'Professional designer profile',
        url: `https://wdhejbul.com/profile/${username}`,
        mainEntity: {
          '@type': 'Person',
          name: userData.fullName,
          jobTitle: userData.occupation || 'Creative Designer',
          description: userData.bio || '',
          image: userData.profilePictureURL || '',
          sameAs: Object.values(userData.socialLinks || {}).filter(Boolean),
          address: userData.location
            ? {
                '@type': 'PostalAddress',
                addressLocality: userData.location,
              }
            : undefined,
        },
      }
    : null;

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-12 h-12 rounded-full border-2 border-t-transparent"
          style={{ borderColor: 'var(--text)' }}
        />
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto px-4"
        >
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <FiUser className="w-12 h-12" style={{ color: 'var(--muted)' }} />
          </div>
          <h1
            className="text-2xl font-bold mb-3"
            style={{ color: 'var(--text)' }}
          >
            Profile Not Found
          </h1>
          <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
            The profile "{username}" doesn't exist or has been removed.
          </p>
          <NavLink
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all hover:gap-3"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            Back to Home <FiArrowRight className="w-4 h-4" />
          </NavLink>
        </motion.div>
      </div>
    );
  }

  const displayUsername = userData.username?.startsWith('@')
    ? userData.username
    : `@${userData.username}`;
  const hasCoverImage =
    userData.coverImageURL &&
    userData.coverImageURL.trim() !== '' &&
    !userData.coverImageURL.startsWith('blob:');

  return (
    <>
      {/*
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/social-preview?page=profile
      */}
      <Helmet>
        <title>{`${userData.fullName || 'Designer'} - WD Hajbul Professional Profile`}</title>
        <meta
          name="description"
          content={
            userData.bio ||
            'View the professional profile of a creative designer specializing in brand identity and product visuals.'
          }
        />
        <meta
          name="keywords"
          content={`profile, designer, ${userData.fullName}, brand designer, product designer, graphic designer, creative, portfolio`}
        />
        <link
          rel="canonical"
          href={`https://wdhejbul.com/profile/${username}`}
        />
        <meta name="robots" content="index, follow" />

        {/* Schema for Profile page */}
        {profileSchema && (
          <script type="application/ld+json">
            {JSON.stringify(profileSchema)}
          </script>
        )}
      </Helmet>

      <div
        className="min-h-screen pb-12 pt-10 overflow-x-hidden"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        {/* ============================================ */}
        {/* PROFILE HEADER - Advanced Architecture */}
        {/* ============================================ */}
        <div className="relative pt-8 max-sm:pt-12 md:pt-20 overflow-hidden">
          {/* Liquid gradient orbs - subtle black & white */}
          <motion.div
            animate={{
              x: ['-10%', '10%', '-10%'],
              y: ['-10%', '15%', '-10%'],
              scale: [1, 1.2, 1],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-20 -left-20 w-64 sm:w-80 md:w-96 h-64 sm:h-80 md:h-96 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(circle, var(--text) 0%, transparent 70%)',
              opacity: 0.03,
            }}
          />
          <motion.div
            animate={{
              x: ['15%', '-10%', '15%'],
              y: ['15%', '-10%', '15%'],
              scale: [1.2, 1, 1.2],
            }}
            transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -bottom-20 -right-20 w-64 sm:w-80 md:w-96 h-64 sm:h-80 md:h-96 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(circle, var(--text) 0%, transparent 70%)',
              opacity: 0.02,
            }}
          />

          <div className="relative max-w-6xl mx-auto px-3 sm:px-4">
            <GlassyCard className="rounded-2xl sm:rounded-3xl overflow-hidden">
              {/* Cover Image - 3:1 Ratio (1500x500px recommended) */}
              <div
                className="relative w-full"
                style={{
                  aspectRatio: '3 / 1',
                  backgroundColor: 'var(--border)',
                  minHeight: '120px',
                }}
              >
                {hasCoverImage ? (
                  <img
                    src={userData.coverImageURL}
                    alt="Cover"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-black/5 via-transparent to-black/5 dark:from-white/5 dark:to-white/5">
                    <div className="absolute inset-0 opacity-5">
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundImage:
                            'radial-gradient(circle at 1px 1px, var(--text) 1px, transparent 0)',
                          backgroundSize: '40px 40px',
                        }}
                      />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center px-4">
                      <div className="text-center">
                        <h1
                          className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tighter"
                          style={{ color: 'var(--text)' }}
                        >
                          {userData.fullName?.toUpperCase()}
                        </h1>
                        <div
                          className="w-12 sm:w-16 h-px mx-auto my-2 sm:my-3"
                          style={{ backgroundColor: 'var(--muted)' }}
                        />
                        <p
                          className="text-[10px] sm:text-xs md:text-sm uppercase tracking-widest"
                          style={{ color: 'var(--muted)' }}
                        >
                          {userData.occupation || 'Creative Designer'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-transparent to-transparent opacity-60" />

                {/* Overlay gradient at bottom for better text readability */}
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[var(--bg)] via-transparent to-transparent opacity-40" />
              </div>

              {/* Profile Info - Overlapping */}
              <div className="relative px-3 sm:px-4 md:px-6 pb-4 sm:pb-5 md:pb-6">
                {/* Profile Picture - Responsive sizing */}
                <div className="relative -mt-12 sm:-mt-14 md:-mt-16 lg:-mt-20 xl:-mt-24 mb-3 sm:mb-4">
                  <div className="relative inline-block">
                    <div
                      className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 xl:w-36 xl:h-36 rounded-full border-3 sm:border-4 overflow-hidden shadow-xl"
                      style={{
                        borderColor: 'var(--bg)',
                        backgroundColor: 'var(--border)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                      }}
                    >
                      {userData.profilePictureURL ? (
                        <img
                          src={userData.profilePictureURL}
                          alt={userData.fullName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FiUser
                            className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14"
                            style={{ color: 'var(--text)' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Name and Bio */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-1.5 sm:gap-2.5 mb-0.5 sm:mb-1">
                      <h1
                        className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold truncate"
                        style={{ color: 'var(--text)' }}
                      >
                        {userData.fullName}
                      </h1>
                      {/* Blue Badge - Right side of name */}
                      <RiVerifiedBadgeFill className="w-5 h-5 sm:w-6 sm:h-6 text-[#1877F2] flex-shrink-0" />
                    </div>
                    <p
                      className="text-xs sm:text-sm"
                      style={{ color: 'var(--muted)' }}
                    >
                      {displayUsername}
                    </p>

                    {userData.bio && (
                      <p
                        className="text-xs sm:text-sm mt-1.5 sm:mt-2 max-w-2xl leading-relaxed"
                        style={{ color: 'var(--text)' }}
                      >
                        {userData.bio}
                      </p>
                    )}

                    {/* Info Chips - Fully visible */}
                    <div
                      className="flex flex-wrap items-center gap-1.5 sm:gap-2 md:gap-3 mt-2 sm:mt-3"
                      style={{ color: 'var(--muted)' }}
                    >
                      {/* Occupation */}
                      <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-[var(--border)] px-1.5 sm:px-2.5 py-0.5 rounded-full whitespace-nowrap">
                        <FiBriefcaseIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                        <span className="text-[8px] sm:text-[10px] md:text-xs whitespace-nowrap">
                          {userData.occupation || 'Designer'}
                        </span>
                      </span>

                      {/* Location */}
                      <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-[var(--border)] px-1.5 sm:px-2.5 py-0.5 rounded-full whitespace-nowrap">
                        <FiMapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                        <span className="text-[8px] sm:text-[10px] md:text-xs whitespace-nowrap">
                          {userData.location || 'Global'}
                        </span>
                      </span>

                      {/* Joined Date - Full date */}
                      <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-[var(--border)] px-1.5 sm:px-2.5 py-0.5 rounded-full whitespace-nowrap">
                        <FiCalendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                        <span className="text-[8px] sm:text-[10px] md:text-xs whitespace-nowrap">
                          {accountCreatedDate ||
                            userData.joinDate ||
                            'Joined 2024'}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons - Responsive */}
                  <div className="flex gap-2 sm:gap-3 flex-shrink-0 mt-2 sm:mt-0 w-full sm:w-auto">
                    <motion.a
                      href="/contact"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all"
                      style={{
                        backgroundColor: 'var(--text)',
                        color: 'var(--bg)',
                      }}
                    >
                      <FiMail className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="text-[10px] sm:text-xs md:text-sm whitespace-nowrap">
                        Contact
                      </span>
                    </motion.a>
                    <motion.a
                      href="/portfolio"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <span className="text-[10px] sm:text-xs md:text-sm whitespace-nowrap">
                        Portfolio
                      </span>
                      <FiArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                    </motion.a>
                  </div>
                </div>
              </div>
            </GlassyCard>
          </div>
        </div>

        <GlassyDivider />

        {/* Stats Section */}
        <StatsSection
          fetchProjectCount={true}
          columns={4}
          variant="default"
          showBorder={true}
        />

        <GlassyDivider />

        {/* Value Proposition - Client-Focused */}
        <section className="py-12 px-4 max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-sm uppercase tracking-wider opacity-60 mb-4 block"
            >
              Why Work With Me
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--text)' }}
            >
              What I{' '}
              <span className="italic" style={{ color: 'var(--muted)' }}>
                bring to the table
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
              style={{ color: 'var(--muted)' }}
            >
              Client-focused values that ensure every project is a success
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {clientValues.map((value, idx) => (
              <ValueCard
                key={idx}
                icon={value.icon}
                title={value.title}
                description={value.description}
              />
            ))}
          </div>
        </section>

        <GlassyDivider />

        {/* About Me */}
        {userData.longBio && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto px-4"
          >
            <GlassyCard className="p-8 rounded-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-1 h-8 rounded-full"
                  style={{ backgroundColor: 'var(--text)' }}
                />
                <h2
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  About Me
                </h2>
              </div>
              <p
                className="text-sm leading-relaxed whitespace-pre-wrap"
                style={{ color: 'var(--muted)' }}
              >
                {userData.longBio}
              </p>
            </GlassyCard>
          </motion.div>
        )}

        {/* Skills */}
        {userData.professionalInfo?.skills?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto px-4 mt-6"
          >
            <GlassyCard className="p-8 rounded-3xl">
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-1 h-8 rounded-full"
                  style={{ backgroundColor: 'var(--text)' }}
                />
                <h2
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  Skills & Expertise
                </h2>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {userData.professionalInfo.skills.map((skill, idx) => (
                  <SkillBadge key={idx} skill={skill} index={idx} />
                ))}
              </div>
            </GlassyCard>
          </motion.div>
        )}

        {/* Certifications & Awards */}
        {(userData.professionalInfo?.certifications?.length > 0 ||
          userData.professionalInfo?.awards?.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto px-4 mt-6"
          >
            <div className="grid md:grid-cols-2 gap-4">
              {userData.professionalInfo?.certifications?.length > 0 && (
                <GlassyCard className="p-8 rounded-3xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-1 h-6 rounded-full"
                      style={{ backgroundColor: 'var(--text)' }}
                    />
                    <FiAwardIcon
                      className="w-5 h-5"
                      style={{ color: 'var(--text)' }}
                    />
                    <h3
                      className="text-lg font-bold"
                      style={{ color: 'var(--text)' }}
                    >
                      Certifications
                    </h3>
                  </div>
                  <div className="space-y-2.5">
                    {userData.professionalInfo.certifications.map(
                      (cert, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          viewport={{ once: true }}
                          className="flex items-center gap-3 text-sm p-3 rounded-xl transition-all hover:translate-x-1 hover:shadow-md"
                          style={{
                            color: 'var(--muted)',
                            backgroundColor: 'var(--border)',
                          }}
                        >
                          <div className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                            <FiCheck className="w-3.5 h-3.5 text-green-500" />
                          </div>
                          {cert}
                        </motion.div>
                      ),
                    )}
                  </div>
                </GlassyCard>
              )}

              {userData.professionalInfo?.awards?.length > 0 && (
                <GlassyCard className="p-8 rounded-3xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-1 h-6 rounded-full"
                      style={{ backgroundColor: 'var(--text)' }}
                    />
                    <FiStarIcon
                      className="w-5 h-5"
                      style={{ color: 'var(--text)' }}
                    />
                    <h3
                      className="text-lg font-bold"
                      style={{ color: 'var(--text)' }}
                    >
                      Awards
                    </h3>
                  </div>
                  <div className="space-y-2.5">
                    {userData.professionalInfo.awards.map((award, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        viewport={{ once: true }}
                        className="flex items-center gap-3 text-sm p-3 rounded-xl transition-all hover:translate-x-1 hover:shadow-md"
                        style={{
                          color: 'var(--muted)',
                          backgroundColor: 'var(--border)',
                        }}
                      >
                        <div className="w-6 h-6 rounded-full bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                          <FiStar className="w-3.5 h-3.5 text-yellow-500" />
                        </div>
                        {award}
                      </motion.div>
                    ))}
                  </div>
                </GlassyCard>
              )}
            </div>
          </motion.div>
        )}

        <GlassyDivider />

        {/* Reviews Section - Reusable Component */}
        <ReviewsSection />

        <GlassyDivider />

        {/* Social Links */}
        {socialPlatforms.some(
          platform => userData.socialLinks?.[platform.id],
        ) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto px-4"
          >
            <GlassyCard className="p-8 rounded-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-1 h-8 rounded-full"
                  style={{ backgroundColor: 'var(--text)' }}
                />
                <h2
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  Connect & Follow
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {socialPlatforms.map(platform => {
                  const url = userData.socialLinks?.[platform.id];
                  if (!url) return null;
                  return (
                    <motion.a
                      key={platform.id}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative group"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.95 }}
                      onMouseEnter={() => setHoveredSocial(platform.id)}
                      onMouseLeave={() => setHoveredSocial(null)}
                    >
                      <div
                        className="flex items-center gap-3 p-3.5 rounded-xl transition-all duration-300 overflow-hidden"
                        style={{
                          backgroundColor: 'var(--bg)',
                          border: '1px solid var(--border)',
                        }}
                      >
                        <motion.div
                          className="flex-shrink-0"
                          animate={{
                            scale: hoveredSocial === platform.id ? 1.2 : 1,
                            rotate: hoveredSocial === platform.id ? 10 : 0,
                          }}
                        >
                          <platform.icon
                            className="w-5 h-5"
                            style={{ color: platform.color }}
                          />
                        </motion.div>
                        <span
                          className="text-sm font-medium flex-1"
                          style={{ color: 'var(--text)' }}
                        >
                          {platform.label}
                        </span>
                        {hoveredSocial === platform.id && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="flex-shrink-0"
                          >
                            <FiExternalLink
                              className="w-3 h-3"
                              style={{ color: 'var(--muted)' }}
                            />
                          </motion.div>
                        )}
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            </GlassyCard>
          </motion.div>
        )}

        <GlassyDivider />

        {/* Fiverr Card */}
        <section className="py-8 px-4 max-w-7xl mx-auto">
          <FiverrCard variant="standard" />
        </section>

        <GlassyDivider />

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto px-4"
        >
          <GlassyCard className="p-12 rounded-3xl text-center relative overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                opacity: 0.02,
              }}
            />

            <div className="relative z-10">
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <FiZap
                  className="w-12 h-12 mx-auto mb-4"
                  style={{ color: 'var(--text)' }}
                />
              </motion.div>
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: 'var(--text)' }}
              >
                Let's Create Together
              </h2>
              <p
                className="text-sm mb-8 max-w-md mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                Have a project in mind? Let's collaborate and bring your vision
                to life.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.03, gap: '0.75rem' }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300"
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
                >
                  Start a Project <FiArrowRight className="w-4 h-4" />
                </motion.a>
                <motion.a
                  href="/portfolio"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  View Portfolio
                </motion.a>
              </div>
            </div>
          </GlassyCard>
        </motion.div>

        <style jsx>{`
          @keyframes shimmer {
            0% {
              transform: translateX(-100%) rotate(12deg);
            }
            100% {
              transform: translateX(200%) rotate(12deg);
            }
          }
        `}</style>
      </div>
    </>
  );
}

export default PublicProfile;
